import type { RequestInfo } from '@ember-data/request';
import { buildBaseURL, type QueryUrlOptions } from '@warp-drive/utilities';
import { SkipCache } from '@warp-drive/core-types/request';

interface UserActionOptions {
  changelog?: string;
}

export function deactivateUserRequest(
  userId: string,
  options: UserActionOptions = {}
): RequestInfo {
  const { changelog } = options;

  const urlOptions: QueryUrlOptions = {
    identifier: { type: 'users' },
    op: 'query',
    resourcePath: 'users',
  };
  const url = buildBaseURL(urlOptions);
  const body = JSON.stringify({ data: { attributes: { _changelog: changelog } } });
  return {
    url: `${url}/${userId}/deactivate-user`,
    method: 'DELETE',
    body,
    cacheOptions: {
      [SkipCache]: true, // ← This tells CacheHandler to skip JSON:API validation
    },
  };
}

export function reactivateUserRequest(
  userId: string,
  options: UserActionOptions = {}
): RequestInfo {
  const { changelog } = options;

  const urlOptions: QueryUrlOptions = {
    identifier: { type: 'users' },
    op: 'query',
    resourcePath: 'users',
  };
  const url = buildBaseURL(urlOptions);
  const body = JSON.stringify({ data: { attributes: { _changelog: changelog } } });
  return {
    url: `${url}/${userId}/reactivate-user`,
    method: 'PATCH',
    body,
    cacheOptions: {
      [SkipCache]: true, // ← This tells CacheHandler to skip JSON:API validation
    },
  };
}

export function wellnessInfoRequest(userId: string): RequestInfo {
  const url = usersBaseUrl();
  return {
    url: `${url}/${userId}/user-profile?profile_key=UserProfileAttribute.WELLNESS_TRAIN`,
    method: 'GET',
    cacheOptions: {
      [SkipCache]: true, // ← This tells CacheHandler to skip JSON:API validation
    },
  };
}

function usersBaseUrl() {
  const urlOptions: QueryUrlOptions = {
    identifier: { type: 'users' },
    op: 'query',
    resourcePath: 'users',
  };

  return buildBaseURL(urlOptions);
}

function wellnessRecalculationBody(userId: string) {
  return JSON.stringify({
    data: {
      type: 'profile-attributes',
      attributes: {},
      relationships: {
        user: {
          data: {
            type: 'users',
            id: userId,
          },
        },
      },
    },
  });
}

function wellnessRecalculationRequest(userId: string, path: string, method: 'PATCH' | 'POST') {
  const url = usersBaseUrl();

  return {
    url: `${url}/${userId}/${path}`,
    method,
    body: wellnessRecalculationBody(userId),
    cacheOptions: {
      [SkipCache]: true, // ← This tells CacheHandler to skip JSON:API validation
    },
  };
}

export function recalculateWellnessRequest(userId: string): RequestInfo {
  return wellnessRecalculationRequest(userId, 'user-wellness-train', 'PATCH');
}

export function enqueueWellnessRecalculationRequest(userId: string): RequestInfo {
  return wellnessRecalculationRequest(userId, 'queue-user-wellness-train', 'POST');
}

/**
 * GET /users/:id/summarize-loans — aggregate snapshot of the user's
 * loans for PSLF/IDR/repayment-strategy dashboards.
 */
export function getLoanSummaryRequest(userId: string): RequestInfo {
  return {
    url: `${usersBaseUrl()}/${userId}/summarize-loans`,
    method: 'GET',
    cacheOptions: { [SkipCache]: true },
  };
}

/** GET /users/zendesk-jwt — short-lived JWT for the Zendesk widget. */
export function getZendeskJwtRequest(): RequestInfo {
  return {
    url: `${usersBaseUrl()}/zendesk-jwt`,
    method: 'GET',
    cacheOptions: { [SkipCache]: true },
  };
}

export function makeTasRecommendationRequest(applicationId: string): RequestInfo {
  const urlOptions: QueryUrlOptions = {
    identifier: { type: 'tas-applications' },
    op: 'query',
    resourcePath: 'tas-applications',
  };
  const url = buildBaseURL(urlOptions);
  const body = JSON.stringify({
    application_id: applicationId,
  });
  return {
    url: `${url}/${applicationId}/recommend`,
    method: 'POST',
    body,
    cacheOptions: {
      [SkipCache]: true, // ← This tells CacheHandler to skip JSON:API validation
    },
  };
}
